/**
 * About — Zoe & PetJr story (full page)
 */
import React, { useState } from 'react';
import { FONT_FAMILY, SHOP_SPACING } from '../ShopStyles';

const About = ({ onNavigate }) => {
  const [heroHovered, setHeroHovered] = useState(false);

  return (
    <div style={s.wrap}>
      <div style={s.hero}>
        <span style={s.sparkle}>✨</span>
        <h1 style={s.title}>Meet Zoe</h1>
        <span style={s.sparkle}>✨</span>
      </div>
      <p style={s.subtitle}>The 9-year-old behind PetJr</p>

      <div
        style={{
          ...s.heroVisual,
          transform: heroHovered ? 'rotate(0deg) scale(1.01)' : 'rotate(-1deg)',
          boxShadow: heroHovered
            ? '0 16px 40px rgba(126, 82, 199, 0.35)'
            : '0 8px 24px rgba(0,0,0,0.12)',
        }}
        onMouseEnter={() => setHeroHovered(true)}
        onMouseLeave={() => setHeroHovered(false)}
      >
        <div style={s.heroEmoji} aria-hidden>
          👩🏻‍🦱
        </div>
        <p style={s.heroCaption}>Designer & dreamer — with cats Temu & Shine 🐱🐱</p>
      </div>

      <div style={s.introCard}>
        <p style={s.introText}>
          Hi! I'm <strong style={s.highlight}>Zoe</strong> and I'm 9 years old. I made PetJr to turn pet
          photos into cool artwork!
        </p>
      </div>

      <div style={s.storyCard}>
        <span style={s.storyEmoji}>🐱</span>
        <p style={s.storyText}>
          I love my two cats, <strong>Temu</strong> and <strong>Shine</strong>. They inspired me to
          create PetJr!
        </p>
      </div>

      <div style={s.designCard}>
        <div style={s.designHeader}>
          <span>🎨</span>
          <h3 style={s.designTitle}>What I designed</h3>
        </div>
        <div style={s.tags}>
          {['Logo', 'Colors', 'Pet styles', 'Website layout'].map((item) => (
            <span key={item} style={s.tag}>
              {item}
            </span>
          ))}
        </div>
      </div>

      <div style={s.dadCard}>
        <h3 style={s.dadTitle}>👨‍👧 Built with Dad!</h3>
        <p style={s.dadText}>
          I told my dad I wanted to make something that could turn pet photos into artwork. He said{' '}
          <strong>&quot;Let&apos;s build it together!&quot;</strong> My dad handles the tricky coding while teaching
          me along the way. We make a great team! 💪
        </p>
      </div>

      <div style={s.journeyCard}>
        <h3 style={s.journeyTitle}>💻 My coding journey</h3>
        <p style={s.journeyIntro}>
          I started with Scratch when I was 5. Now I’m learning real languages with Dad:
        </p>
        <div style={s.skills}>
          {[
            { emoji: '🦴', name: 'HTML' },
            { emoji: '🎨', name: 'CSS' },
            { emoji: '⚡', name: 'JavaScript' },
            { emoji: '⚛️', name: 'React' },
          ].map((x) => (
            <div key={x.name} style={s.skill}>
              <span>{x.emoji}</span>
              <span style={s.skillName}>{x.name}</span>
            </div>
          ))}
        </div>
      </div>

      <p style={s.thanks}>Thanks for using PetJr! 🐕 💖 🐈</p>

      <button type="button" style={s.cta} onClick={() => onNavigate?.('home')}>
        <span>Back to home</span>
        <span>🐾</span>
      </button>
    </div>
  );
};

const s = {
  wrap: {
    maxWidth: '640px',
    margin: '0 auto',
    paddingBottom: SHOP_SPACING.xxl,
    fontFamily: FONT_FAMILY,
  },
  hero: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    marginBottom: '4px',
  },
  sparkle: { fontSize: '24px' },
  title: {
    fontSize: 'clamp(28px, 5vw, 36px)',
    fontWeight: '800',
    margin: 0,
    background: 'linear-gradient(135deg, #7E52C7 0%, #A389F4 50%, #FF6B6B 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  subtitle: {
    textAlign: 'center',
    fontSize: '15px',
    color: '#666',
    marginBottom: SHOP_SPACING.lg,
  },
  heroVisual: {
    borderRadius: '18px',
    padding: SHOP_SPACING.lg,
    background: 'linear-gradient(135deg, #FFE36A 0%, #FF9E4A 50%, #FF6B6B 100%)',
    marginBottom: SHOP_SPACING.md,
    transition: 'all 0.3s ease',
    textAlign: 'center',
  },
  heroEmoji: {
    fontSize: 'clamp(64px, 15vw, 120px)',
    lineHeight: 1.1,
    marginBottom: SHOP_SPACING.sm,
  },
  heroCaption: {
    margin: 0,
    fontSize: '15px',
    fontWeight: '600',
    color: '#431407',
    fontFamily: FONT_FAMILY,
  },
  introCard: {
    background: 'linear-gradient(135deg, #f8f5ff 0%, #fff 100%)',
    borderRadius: '16px',
    padding: '16px 18px',
    marginBottom: SHOP_SPACING.md,
    border: '2px solid rgba(126, 82, 199, 0.12)',
  },
  introText: {
    fontSize: '16px',
    lineHeight: 1.55,
    color: '#333',
    margin: 0,
    textAlign: 'center',
  },
  highlight: { color: '#7E52C7', fontWeight: '700' },
  storyCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    background: '#fff9e6',
    borderRadius: '14px',
    padding: '14px 16px',
    marginBottom: SHOP_SPACING.md,
    border: '2px solid rgba(255, 227, 106, 0.35)',
  },
  storyEmoji: { fontSize: '28px', flexShrink: 0 },
  storyText: {
    fontSize: '15px',
    lineHeight: 1.5,
    color: '#555',
    margin: 0,
  },
  designCard: {
    background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
    borderRadius: '16px',
    padding: '16px',
    marginBottom: SHOP_SPACING.md,
  },
  designHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '10px',
    fontSize: '20px',
  },
  designTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#92400e',
    margin: 0,
  },
  tags: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  tag: {
    background: '#fff',
    color: '#b45309',
    fontSize: '13px',
    fontWeight: '600',
    padding: '5px 12px',
    borderRadius: '18px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
  },
  dadCard: {
    background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
    borderRadius: '16px',
    padding: '16px 18px',
    marginBottom: SHOP_SPACING.md,
    border: '2px solid rgba(59, 130, 246, 0.2)',
  },
  dadTitle: {
    fontSize: '17px',
    fontWeight: '700',
    color: '#1e40af',
    margin: '0 0 10px 0',
  },
  dadText: {
    fontSize: '15px',
    lineHeight: 1.55,
    color: '#1e3a8a',
    margin: 0,
  },
  journeyCard: {
    background: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)',
    borderRadius: '16px',
    padding: '16px 18px',
    marginBottom: SHOP_SPACING.lg,
  },
  journeyTitle: {
    fontSize: '17px',
    fontWeight: '700',
    color: '#5b21b6',
    margin: '0 0 8px 0',
    textAlign: 'center',
  },
  journeyIntro: {
    fontSize: '14px',
    color: '#6b21a8',
    textAlign: 'center',
    margin: '0 0 12px 0',
  },
  skills: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '8px',
  },
  skill: {
    background: '#fff',
    borderRadius: '10px',
    padding: '8px 10px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '18px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
  },
  skillName: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#7c3aed',
  },
  thanks: {
    textAlign: 'center',
    fontSize: '18px',
    fontWeight: '700',
    color: '#333',
    marginBottom: SHOP_SPACING.md,
  },
  cta: {
    width: '100%',
    padding: '14px 20px',
    fontSize: '16px',
    fontWeight: '700',
    fontFamily: FONT_FAMILY,
    background: 'linear-gradient(135deg, #7E52C7 0%, #A389F4 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '14px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    boxShadow: '0 6px 20px rgba(126, 82, 199, 0.3)',
  },
};

export default About;
